import React, { useState } from 'react';

import displayCurrency from '../../../../helpers/displayCurrency';
import displayName from '../../../../helpers/displayName';
import {
  getOrganisationId,
  isBroker,
  isBrokerOrganisation,
  isOwner,
} from '../../../auth/redux/authActions';
import './SmartLuggagePremiumBreakdown.scss';
import { Col, Modal, Row } from 'react-bootstrap';
import cx from 'classnames';
import { isNumber } from 'redux-form-validators/src/helpers';
import { Button } from '../../../../common/components';

const Calculation = ({ formula, calculation, errors, breakdown }) => (
  <Row>
    <Col xs={12} className="calculation-container">
      <Row>
        <Col xs={2}>Formula</Col>
        <Col xs={9}>{formula}</Col>
      </Row>
      <Row>
        <Col xs={2}>Calculation</Col>
        <Col xs={9}>{calculation}</Col>
      </Row>

      {errors && !!errors.length && (
        <Row>
          <Col xs={2}>Errors</Col>
          <Col xs={9}>
            <ul className="option-list">
              {errors.map((error, key) => {
                return <li key={'error' + key}>{error}</li>;
              })}
            </ul>
          </Col>
        </Row>
      )}
      {breakdown.categories && (
        <Row>
          <Col xs={2}>Categories</Col>

          <Col xs={9}>
            {breakdown.categories.map((category, key) => {
              return (
                <div key={'category' + key}>
                  <Col xs={2} className="no-gutter">
                    Calculation:
                  </Col>
                  <Col xs={10}>{category.calc}</Col>
                  <Col xs={2} className="no-gutter">
                    Rates:
                  </Col>
                  <Col xs={10}>
                    {category.rates.map((rate, rateKey) => {
                      return (
                        <span key={'rate' + rateKey}>
                          {rate}
                          <br />
                        </span>
                      );
                    })}
                  </Col>
                </div>
              );
            })}
          </Col>
        </Row>
      )}
      {breakdown &&
        Object.keys(breakdown).map((key) => {
          const keysToIgnore = ['categories', 'options'];

          if (keysToIgnore.indexOf(key) === -1) {
            const breakdownItem = breakdown[key];
            return (
              <Row key={'breakdown-row ' + key}>
                <Col xs={2}>{displayName(key)}</Col>
                <Col xs={9}>
                  <ul className="option-list">
                    {breakdownItem &&
                      Object.keys(breakdownItem).map((breakdownItemKey) => {
                        const breakdownItemKeyName = !isNumber(breakdownItemKey)
                          ? `${displayName(breakdownItemKey)} -`
                          : '';
                        return (
                          <li key={'breakdown-item ' + breakdownItemKey}>
                            {`${breakdownItemKeyName} ${breakdownItem[breakdownItemKey]}`}
                          </li>
                        );
                      })}
                  </ul>
                </Col>
              </Row>
            );
          }
        })}
    </Col>
  </Row>
);

const PremiumElementRow = ({ label, value, currencyCode, isSubTotal }) => (
  <Row>
    <Col
      xs={7}
      className={cx({
        'sub-total': isSubTotal,
      })}
    >
      {label}
    </Col>
    <Col
      xs={4}
      className={cx('text-right', {
        'sub-total': isSubTotal,
      })}
    >
      {displayCurrency(value, currencyCode)}
    </Col>
  </Row>
);

const Commissions = ({ commissions, currencyCode }) => {
  return (
    <div className="commission-breakdown">
      {commissions.map((commission, index) => {
        const type =
          commission.commission_type === 'owner'
            ? 'Just Travel'
            : commission.commission_type;
        const isAdjusted = commission.effective_rate !== commission.rate;
        const rate =
          (commission.effective_rate ? commission.effective_rate : commission.rate) * 100;

        return (
          <Row key={commission.organisation_id}>
            <Col sm={7} className="commission-type">
              {`${type} Commission @ ${rate.toFixed(1)}% ${
                isAdjusted ? '(adjusted)' : ''
              }`}
            </Col>
            <Col
              sm={2}
              className={cx('text-right', {
                'commission-amount-last': index === commissions.length - 1,
              })}
            >
              {displayCurrency(commission.amount, currencyCode)}
            </Col>
          </Row>
        );
      })}
    </div>
  );
};
const BrokerBreakdown = ({
  premium,
  currencyCode,
  schemeName,
  show,
  taxRate,
  commissions,
}) => {
  if (!show) {
    return null;
  }

  const commission = commissions.find((commission) => {
    return commission.organisation_id === getOrganisationId();
  });
  const isAdjusted =
    commission.effective_rate && commission.effective_rate !== commission.rate;
  const rate =
    (commission.effective_rate ? commission.effective_rate : commission.rate) * 100;

  return (
    <Row>
      <Col sm={12} className="sl-premium-summary-container">
        <h4>{schemeName}</h4>
        <PremiumElementRow
          value={premium.principal}
          currencyCode={currencyCode}
          label={'Principal'}
        />
        <PremiumElementRow
          value={premium.ex_tax}
          currencyCode={premium.currency}
          label={'Admin Fee'}
        />

        <PremiumElementRow
          value={commission.amount}
          currencyCode={currencyCode}
          label={`Commission @ ${rate.toFixed(1)}% ${isAdjusted ? '(adjusted)' : ''}`}
          isSubTotal
        />
        <PremiumElementRow
          value={premium.tax}
          currencyCode={currencyCode}
          label={`IPT @ ${taxRate}`}
        />
        <PremiumElementRow
          value={premium.gross}
          currencyCode={currencyCode}
          label={'Gross Premium'}
          isSubTotal
        />
      </Col>
    </Row>
  );
};
const OwnerBreakdown = ({
  premium,
  currencyCode,
  schemeName,
  show,
  taxRate,
  commissions,
  formula,
  calculation,
  errors,
  breakdown,
}) => {
  const [expandCommissions, setExpandCommissions] = useState(false);
  if (!show) {
    return null;
  }
  const toggleCommissions = () => {
    setExpandCommissions(!expandCommissions);
  };
  return (
    <div className="breakdown">
      <Row>
        <Col xs={12} className="sl-premium-summary-container">
          <h4>{schemeName}</h4>
          <PremiumElementRow
            value={premium.net}
            currencyCode={currencyCode}
            label={'Net'}
          />
          <PremiumElementRow
            value={premium.additional}
            currencyCode={currencyCode}
            label={'Additional'}
          />
          <PremiumElementRow
            value={premium.medical}
            currencyCode={currencyCode}
            label={'Medical'}
          />
          <PremiumElementRow
            value={premium.discount}
            currencyCode={currencyCode}
            label={'Discount'}
          />

          {expandCommissions && commissions && (
            <Commissions commissions={commissions} currenyCode={currencyCode} />
          )}
          <div className="row">
            <div className="col-xs-7">Commission @ {premium.commission}&#37;</div>
            <div className="col-xs-4 text-right">
              {displayCurrency(premium.commission, currencyCode)}
            </div>
            <div className="col-xs-1">
              <i
                className={
                  'expand-commissions fa fa-' +
                  (expandCommissions ? 'minus-square' : 'plus-square')
                }
                onClick={toggleCommissions}
              />
            </div>
          </div>

          <PremiumElementRow
            value={premium.principal}
            currencyCode={currencyCode}
            label={'Principal'}
            isSubTotal
          />

          <PremiumElementRow
            value={premium.tax}
            currencyCode={currencyCode}
            label={`IPT @ ${taxRate}%`}
          />

          <PremiumElementRow
            value={premium.ex_tax}
            currencyCode={currencyCode}
            label={'Admin Fee'}
          />
          <PremiumElementRow
            value={premium.gross}
            currencyCode={currencyCode}
            label={'Gross Premium'}
            isSubTotal
          />
        </Col>
      </Row>
      <Calculation
        formula={formula}
        calculation={calculation}
        errors={errors}
        breakdown={breakdown}
      />
    </div>
  );
};
const PremiumBreakdown = ({ premium }) => {
  const [show, setShow] = useState(false);

  const showModal = () => {
    setShow(true);
  };
  const hideModal = () => {
    setShow(false);
  };
  if (!premium) {
    return;
  }

  const premiumBreakdown = premium.premium;
  return (
    <div>
      <span className={'sl-premium-breakdown-button'} onClick={showModal}>
        {displayCurrency(premiumBreakdown.gross, premiumBreakdown.currency)}
      </span>
      <Modal show={show} onHide={hideModal}>
        <Modal.Header closeButton>
          <Modal.Title>Premium Breakdown</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OwnerBreakdown
            show={isOwner()}
            premium={premiumBreakdown}
            currencyCode={premiumBreakdown.currency}
            schemeName={premium.scheme.name}
            taxRate={premium.rates.tax_rate}
            commissions={premium.commissions}
            errors={premium.errors}
            formula={premium.formula}
            calculation={premium.calculation}
            breakdown={premium.breakdown}
          />

          <BrokerBreakdown
            show={isBroker() || isBrokerOrganisation()}
            premium={premiumBreakdown}
            currencyCode={premiumBreakdown.currency}
            schemeName={premium.scheme.name}
            taxRate={premium.rates.tax_rate}
            commissions={premium.commissions}
            errors={premium.errors}
            formula={premium.formula}
            calculation={premium.calculation}
            breakdown={premium.breakdown}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle={'primary'} label={'Close'} handleClick={hideModal} />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PremiumBreakdown;
